import { ModalHeader } from "../components/common/modalHeader";
import { useParams } from "react-router-dom";
import { useCheckInUsers } from "../hooks/useCheckInUsers";
import { TicketCheckInStatus } from "../models/enum/ticketCheckInStatus";
import { Loading } from "../components/common/Loading";
import { UserListWithCheckBox } from "../components/notCheckedIn/userListWithCheckBox";
import { useTranslation } from "react-i18next";
import { Button } from "../components/common/button";
import { useRegisterNoShow } from "../hooks/useRegisterNoShow";
import { WinningType } from "../models/enum/winningType";
import { useEffect, useRef, useState } from "react";
import { WinnerCheckInStatus } from "../models/winnerCheckInStatus";
import {
  Dialog,
  DialogActions,
  DialogMessage,
} from "../components/common/dialog";
import { WinnerIdInfo } from "../models/winnerIdInfo";
import { NetworkError } from "../models/networkError";
import { toast } from "react-toastify";

export function NotCheckedInList() {
  const { artistId, eventId, rewardId } = useParams();
  const artistIdNum = Number(artistId) ?? 0;
  const eventIdNum = Number(eventId) ?? 0;
  const rewardIdNum = Number(rewardId) ?? 0;
  const { checkInUsers, refetch, isLoading } = useCheckInUsers({
    artistId: artistIdNum,
    eventId: eventIdNum,
    rewardId: rewardIdNum,
    status: TicketCheckInStatus.NOT_CHECKED_IN,
  });
  const { t } = useTranslation();
  const { registerNoShow, isPending } = useRegisterNoShow({
    artistId: artistIdNum,
    eventId: eventIdNum,
    rewardId: rewardIdNum,
  });
  const [primaries, setPrimaries] = useState<WinnerCheckInStatus[]>();
  const [waitingList, setWaitingList] = useState<WinnerCheckInStatus[]>();
  const confirmRef = useRef<HTMLDialogElement>(null);
  let selectedList: WinnerIdInfo[];

  useEffect(() => {
    setPrimaries(
      checkInUsers?.filter((user) => user.winnerType === WinningType.REGULAR),
    );
    setWaitingList(
      checkInUsers?.filter((user) => user.winnerType === WinningType.SPARE),
    );
  }, [checkInUsers]);

  const onRegisterNoShow = () => {
    registerNoShow(selectedList)
      .then(async () => {
        await refetch();
      })
      .catch((e) => {
        if (e instanceof NetworkError) {
          switch (e.body?.code) {
            case "PENALTY_001": {
              toast.error(
                t("notcheckedin_error_checkedin", { n: e.body.args[0] }),
              );
              break;
            }
            case "PENALTY_003": {
              toast.error(
                t("notcheckedin_error_alreadynoshow", { n: e.body.args[0] }),
              );
              break;
            }
            default: {
              toast.error(t("error_server_general"));
            }
          }
        }
      });
  };

  return (
    <div className={"flex flex-col h-full"}>
      <ModalHeader title={t("notcheckedin_subtitle")} />
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className={"p-3 flex-col flex gap-3 overflow-y-auto"}>
            <UserListWithCheckBox
              title={t("eventdetail_checkincount_regular")}
              list={primaries}
              onAction={(list) => {
                selectedList = list;
                confirmRef.current?.showModal();
              }}
              loading={isPending}
            />
            <UserListWithCheckBox
              title={t("eventdetail_checkincount_standby")}
              list={waitingList}
            />
          </div>
          <div
            className={
              "fixed top-0 bottom-0 left-0 right-0 pointer-events-none"
            }
          >
            <Button
              className={
                "btn-primary btn-circle absolute bottom-5 right-5 pointer-events-auto"
              }
              onClick={() => refetch()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="size-4"
              >
                <path
                  fillRule="evenodd"
                  d="M13.836 2.477a.75.75 0 0 1 .75.75v3.182a.75.75 0 0 1-.75.75h-3.182a.75.75 0 0 1 0-1.5h1.37l-.84-.841a4.5 4.5 0 0 0-7.08.932.75.75 0 0 1-1.3-.75 6 6 0 0 1 9.44-1.242l.842.84V3.227a.75.75 0 0 1 .75-.75Zm-.911 7.5A.75.75 0 0 1 13.199 11a6 6 0 0 1-9.44 1.241l-.84-.84v1.371a.75.75 0 0 1-1.5 0V9.591a.75.75 0 0 1 .75-.75H5.35a.75.75 0 0 1 0 1.5H3.98l.841.841a4.5 4.5 0 0 0 7.08-.932.75.75 0 0 1 1.025-.273Z"
                  clipRule="evenodd"
                />
              </svg>
            </Button>
          </div>
          <Dialog ref={confirmRef}>
            <DialogMessage>
              {t("notcheckedin_dialog_absentsubmit")}
            </DialogMessage>
            <DialogActions>
              <div className={"flex gap-2 justify-end w-full"}>
                <button className={"btn basis-1/4"}>
                  {t("common_dialog_cancel")}
                </button>
                <Button
                  className={"btn btn-primary basis-1/4"}
                  onClick={onRegisterNoShow}
                >
                  {t("common_dialog_yes")}
                </Button>
              </div>
            </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
}
